import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingTaxProfileResponseData,
  UpdateOrganizationBillingTaxProfileRequest,
} from '@fluenceur/types';
import { ErrorCode } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { z } from 'zod';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { toaster } from '@/components/ui/toaster';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import {
  billingTaxProfileFieldsSchema,
  BILLING_TAX_PROFILE_QUERY_KEY,
  readBillingTaxProfile,
} from '@/hooks/queries/use-billing-tax-profile-query';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';

const requestSchema = billingTaxProfileFieldsSchema
  .extend({ expectedProfileId: z.string().uuid().nullable() })
  .strict();

export function useUpdateBillingTaxProfile({
  context,
  snapshot,
}: {
  context: OrganizationBillingContext;
  snapshot?: OrganizationBillingTaxProfileResponseData;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingActionScope();
  const profileKey = [
    ...BILLING_TAX_PROFILE_QUERY_KEY,
    scope.userId,
    scope.organizationId,
    scope.brandId,
  ];
  return useMutation<
    OrganizationBillingTaxProfileResponseData,
    AxiosError<ApiResponse> | Error,
    UpdateOrganizationBillingTaxProfileRequest,
    typeof scope
  >({
    mutationKey: [
      ...BILLING_TAX_PROFILE_QUERY_KEY,
      'update',
      scope.userId,
      scope.organizationId,
      scope.brandId,
    ],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (request) => {
      assertCurrent(scope);
      const known = readBillingTaxProfile(snapshot);
      const valid = requestSchema.safeParse(request);
      const fixedCountry = context.billingCountry ?? known?.profile?.country;
      if (
        !scope.userId ||
        !scope.organizationId ||
        !scope.brandId ||
        !readBillingContext(context) ||
        context.channel !== 'stripe' ||
        context.organizationId !== scope.organizationId ||
        context.brandId !== scope.brandId ||
        !context.capabilities.canCommitPaidPlan ||
        context.cardSetup.exemption === 'impersonation' ||
        !known?.canManage ||
        known.organizationId !== scope.organizationId ||
        known.brandId !== scope.brandId ||
        !valid.success ||
        valid.data.expectedProfileId !== (known.profile?.id ?? null) ||
        (fixedCountry && valid.data.country !== fixedCountry)
      ) {
        throw new Error('No authorized organization tax profile update');
      }
      const response = await api.put<ApiResponse<unknown>>(
        '/dashboard/billing/tax-profile',
        valid.data
      );
      assertCurrent(scope);
      const data = readBillingTaxProfile(response.data.data);
      if (
        response.data.success !== true ||
        !data?.profile ||
        data.organizationId !== scope.organizationId ||
        data.brandId !== scope.brandId
      ) {
        throw new Error('Invalid organization tax profile update');
      }
      return data;
    },
    onSuccess: async (data, _request, target) => {
      if (!target) throw new BillingTargetChangedError('Billing target unavailable');
      assertCurrent(target);
      client.setQueryData(profileKey, data);
      await client.invalidateQueries({
        queryKey: billingContextQueryKey(target),
        exact: true,
      });
      assertCurrent(target);
      toaster.success({ title: t('common.success'), description: t('billing.taxProfile.saved') });
    },
    onError: async (error, _request, target) => {
      if (error instanceof BillingTargetChangedError || !target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      const response = (error as AxiosError<ApiResponse>).response;
      const conflict =
        response?.status === 409 &&
        response.data?.code === ErrorCode.E_STRIPE_BILLING_TAX_PROFILE_CONFLICT;
      toaster.error({
        title: t('common.error'),
        description: t(conflict ? 'billing.taxProfile.conflict' : 'billing.taxProfile.saveError'),
      });
      if (conflict) {
        await client.invalidateQueries({ queryKey: profileKey, exact: true });
        try {
          assertCurrent(target);
        } catch {
          return;
        }
      }
    },
  });
}
